import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { routes } from 'client-config';
import { GroupType } from 'graphql-query-contracts';
import { useCostOffsetCode, useUpdateCostOffsetCode } from 'react-hooks';
import { useChannelAdminContext } from 'hooks';
import { AdminPage, PageHeader } from 'components/layout';

import {
  CostOffsetCodeForm,
  type CostOffsetCodeFormValues,
} from './CostOffsetCodeForm';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
  className?: string;
};

export function EditCostOffsetCodeForm({ breadcrumbs, className }: Props) {
  const history = useHistory();
  const { costOffsetCodeId } = useParams<{ costOffsetCodeId: string }>();
  const { channel } = useChannelAdminContext();
  const { t } = useTranslation();

  const channelSlug = channel?.slug || '';
  const productServicesLink = routes.channelAdminProductsServices.replace(
    ':id',
    channelSlug
  );

  const defaultBreadcrumbs = [
    {
      label: t('adp.productsServices.breadcrumb'),
      url: productServicesLink,
    },
    { label: t('adp.productsServices.editCostOffsetCode.breadcrumb') },
  ];

  const { fetchCostOffsetCode, costOffsetCode, loading: loadLoading, error: loadError } =
    useCostOffsetCode();
  const { updateCostOffsetCode, loading: updateLoading } =
    useUpdateCostOffsetCode();

  const [submitError, setSubmitError] = useState<
    { severity: 'warning' | 'error'; error: Error } | undefined
  >(undefined);

  useEffect(() => {
    if (!channel?._id || !costOffsetCodeId) return;

    fetchCostOffsetCode({
      id: costOffsetCodeId,
      groupId: channel._id,
      groupType: GroupType.GroupTypeActivateChannel,
    }).catch(() => {});
  }, [channel?._id, costOffsetCodeId, fetchCostOffsetCode]);

  useEffect(() => {
    if (loadError) {
      window.Toast.show(
        t('abp.productsServices.editCostOffsetCode.loadError'),
        'error'
      );
      history.goBack();
    }
  }, [loadError, history, t]);

  const handleUpdate = async (data: CostOffsetCodeFormValues) => {
    if (!channel?._id || !costOffsetCodeId) return;

    setSubmitError(undefined);

    try {
      await updateCostOffsetCode({
        id: costOffsetCodeId,
        code: data.code.trim(),
        description: data.description?.trim()
          ? data.description.trim()
          : undefined,
        groupId: channel._id,
        groupType: GroupType.GroupTypeActivateChannel,
      });

      window.Toast.show(
        t('abp.productsServices.editCostOffsetCode.updateSuccessful')
      );
      history.goBack();
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error(
              typeof err === 'string' ? err : 'An unexpected error occurred'
            );

      setSubmitError({ severity: 'warning', error });
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  if (loadLoading || !costOffsetCode) {
    return (
      <AdminPage className={className}>
        <PageHeader
          externalPadding={[0, 0]}
          header={t('abp.productsServices.editCostOffsetCode.heading')}
          headerLevel="h3"
          breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage className={className}>
      <PageHeader
        externalPadding={[0, 0]}
        header={t('abp.productsServices.editCostOffsetCode.heading')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
      />
      <CostOffsetCodeForm
        costOffsetCodeData={{
          code: costOffsetCode.code,
          description: costOffsetCode.description,
        }}
        error={submitError}
        disabled={updateLoading}
        onSubmit={handleUpdate}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
}
